import * as THREE from "three";

export type AABB = {
  min: THREE.Vector3;
  max: THREE.Vector3;
  oneWay?: boolean;
};

const tmp = new THREE.Vector3();

export function makeAABB(x: number, y: number, z: number, sx: number, sy: number, sz: number): AABB {
  const hx = sx * 0.5,
    hy = sy * 0.5,
    hz = sz * 0.5;
  return {
    min: new THREE.Vector3(x - hx, y - hy, z - hz),
    max: new THREE.Vector3(x + hx, y + hy, z + hz),
  };
}

export function pointInAABB(p: THREE.Vector3, b: AABB) {
  return p.x >= b.min.x && p.x <= b.max.x && p.y >= b.min.y && p.y <= b.max.y && p.z >= b.min.z && p.z <= b.max.z;
}

export function rayAABB(orig: THREE.Vector3, dir: THREE.Vector3, b: AABB, maxDist: number) {
  let tmin = 0;
  let tmax = maxDist;
  for (let i = 0; i < 3; i++) {
    const o = orig.getComponent(i);
    const d = dir.getComponent(i);
    const min = b.min.getComponent(i);
    const max = b.max.getComponent(i);
    if (Math.abs(d) < 1e-8) {
      if (o < min || o > max) return null;
    } else {
      const inv = 1 / d;
      let t1 = (min - o) * inv;
      let t2 = (max - o) * inv;
      if (t1 > t2) [t1, t2] = [t2, t1];
      tmin = Math.max(tmin, t1);
      tmax = Math.min(tmax, t2);
      if (tmin > tmax) return null;
    }
  }
  return tmin;
}

function closestOnAABB(p: THREE.Vector3, b: AABB, out: THREE.Vector3) {
  out.x = Math.min(Math.max(p.x, b.min.x), b.max.x);
  out.y = Math.min(Math.max(p.y, b.min.y), b.max.y);
  out.z = Math.min(Math.max(p.z, b.min.z), b.max.z);
  return out;
}

export class CharacterMover {
  pos = new THREE.Vector3(0, 0, 0);
  vel = new THREE.Vector3();
  grounded = false;
  radius: number;
  height: number;
  private hit = new THREE.Vector3();

  constructor(radius: number, height: number) {
    this.radius = radius;
    this.height = height;
  }

  private collideAxis(axis: "x" | "y" | "z", boxes: AABB[]) {
    const r = this.radius;
    const h = this.height;
    const y0 = this.pos.y + r;
    const y1 = this.pos.y + h - r;
    for (const b of boxes) {
      const expanded = {
        minx: b.min.x - r,
        maxx: b.max.x + r,
        minz: b.min.z - r,
        maxz: b.max.z + r,
        miny: b.min.y,
        maxy: b.max.y,
      };
      if (this.pos.x < expanded.minx || this.pos.x > expanded.maxx) continue;
      if (this.pos.z < expanded.minz || this.pos.z > expanded.maxz) continue;
      if (y1 < expanded.miny || y0 > expanded.maxy) continue;

      if (axis === "x") {
        const cx = (b.min.x + b.max.x) * 0.5;
        if (this.pos.x < cx) this.pos.x = expanded.minx - 0.0001;
        else this.pos.x = expanded.maxx + 0.0001;
        this.vel.x = 0;
      } else if (axis === "z") {
        const cz = (b.min.z + b.max.z) * 0.5;
        if (this.pos.z < cz) this.pos.z = expanded.minz - 0.0001;
        else this.pos.z = expanded.maxz + 0.0001;
        this.vel.z = 0;
      } else {
        const feet = this.pos.y;
        const head = this.pos.y + h;
        const midY = (b.min.y + b.max.y) * 0.5;
        if (this.vel.y <= 0 && feet < b.max.y && head > b.max.y && midY < feet + 0.6) {
          this.pos.y = b.max.y;
          this.vel.y = 0;
          this.grounded = true;
        } else if (this.vel.y > 0 && head > b.min.y && feet < b.min.y) {
          this.pos.y = b.min.y - h;
          this.vel.y = 0;
        }
      }
    }
  }

  move(dt: number, boxes: AABB[]) {
    this.grounded = false;
    this.pos.x += this.vel.x * dt;
    this.collideAxis("x", boxes);
    this.pos.z += this.vel.z * dt;
    this.collideAxis("z", boxes);
    this.pos.y += this.vel.y * dt;
    this.collideAxis("y", boxes);
    if (this.pos.y < 0) {
      this.pos.y = 0;
      this.vel.y = 0;
      this.grounded = true;
    }
  }

  overlaps(boxes: AABB[]) {
    const p = tmp.set(this.pos.x, this.pos.y + this.height * 0.5, this.pos.z);
    for (const b of boxes) {
      closestOnAABB(p, b, this.hit);
      const dx = p.x - this.hit.x;
      const dz = p.z - this.hit.z;
      const dy = Math.max(b.min.y - (this.pos.y + this.height), this.pos.y - b.max.y);
      if (dx * dx + dz * dz < this.radius * this.radius && dy < 0) return true;
    }
    return false;
  }
}

export function hitscan(
  origin: THREE.Vector3,
  dir: THREE.Vector3,
  maxDist: number,
  boxes: AABB[],
  ignore?: (b: AABB) => boolean
) {
  let best = maxDist;
  let hit: AABB | null = null;
  for (const b of boxes) {
    if (ignore?.(b)) continue;
    const t = rayAABB(origin, dir, b, best);
    if (t !== null && t < best) {
      best = t;
      hit = b;
    }
  }
  if (!hit) return null;
  const point = origin.clone().addScaledVector(dir, best);
  return { dist: best, point, box: hit };
}
